// BM-H80 P2 — HaikuAssistantPanel.
//
// Sidebar panel with three mode tabs: define, suggest wording,
// next-step guidance. Every response is chip-badged
// "draft, not legal advice". Suggest mode adds a "Park as
// suggestion" button that stores the response under
// haiku_suggestions[field_id] on the form instance (server-side).
// Next-step mode always shows the cited engine rule id inline.
//
// The panel is stateless w.r.t. the outer form: it invokes the
// callback ``onAsk`` and renders whatever the caller passes back.
// The parent page owns the axios call so the panel is trivial to
// unit-test in isolation.

import { useState } from "react";
import { HelpCircle, Lightbulb, ListChecks, Sparkles, Loader2 } from "lucide-react";
import { errorMessage } from "../../utils/errorMessage";

const MODES = [
  { id: "define", label: "Define", icon: HelpCircle,
    hint: "Ask what a field or legal term means." },
  { id: "suggest", label: "Suggest wording", icon: Lightbulb,
    hint: "Get a candidate draft wording for a free-text field." },
  { id: "next_step", label: "Next step", icon: ListChecks,
    hint: "Ask what to answer next; grounded in the engine result." },
];

// eslint-disable-next-line react/prop-types
export default function HaikuAssistantPanel({ onAsk, currentFieldId }) {
  const [mode, setMode] = useState("define");
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canAsk = question.trim().length > 0 && !loading;

  async function handleAsk(parkAsSuggestion = false) {
    setLoading(true);
    setError(null);
    try {
      const outcome = await onAsk({
        mode,
        question: question.trim(),
        fieldId: currentFieldId,
        parkAsSuggestion,
      });
      setResponse(outcome);
    } catch (err) {
      const detail = errorMessage(err, "Request failed");
      setError(String(detail));
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <aside
      data-testid="haiku-assistant-panel"
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 16,
        background: "#f8fafc",
        fontSize: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Sparkles size={16} color="#4338ca" />
        <strong style={{ color: "#0f172a" }}>Ask the assistant</strong>
      </div>

      <div role="tablist" style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        {MODES.map((m) => {
          const active = mode === m.id;
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              type="button"
              role="tab"
              data-testid={`haiku-mode-${m.id}`}
              onClick={() => {
                setMode(m.id);
                setResponse(null);
              }}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                padding: "6px 8px",
                borderRadius: 6,
                border: active ? "1px solid #4338ca" : "1px solid #cbd5e1",
                background: active ? "#4338ca" : "#fff",
                color: active ? "#fff" : "#334155",
                cursor: "pointer",
                fontSize: 12,
              }}
              aria-pressed={active}
            >
              <Icon size={14} />
              {m.label}
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
        {MODES.find((m) => m.id === mode).hint}
      </div>

      <textarea
        data-testid="haiku-question-input"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Type your question here"
        rows={3}
        style={{
          width: "100%",
          padding: 8,
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          fontFamily: "inherit",
          fontSize: 14,
          resize: "vertical",
        }}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button
          type="button"
          data-testid="haiku-ask-btn"
          onClick={() => handleAsk(false)}
          disabled={!canAsk}
          style={{
            padding: "8px 14px",
            borderRadius: 6,
            border: "none",
            background: canAsk ? "#4338ca" : "#94a3b8",
            color: "#fff",
            cursor: canAsk ? "pointer" : "not-allowed",
            fontWeight: 600,
          }}
        >
          {loading ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Loader2 size={14} className="animate-spin" />
              Asking
            </span>
          ) : (
            "Ask"
          )}
        </button>
        {mode === "suggest" && currentFieldId && (
          <button
            type="button"
            data-testid="haiku-park-btn"
            onClick={() => handleAsk(true)}
            disabled={!canAsk}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "1px solid #4338ca",
              background: "#fff",
              color: "#4338ca",
              cursor: canAsk ? "pointer" : "not-allowed",
              fontWeight: 600,
            }}
          >
            Ask + park
          </button>
        )}
      </div>

      {error && (
        <div
          data-testid="haiku-error"
          style={{
            marginTop: 12,
            padding: 10,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 6,
            color: "#7f1d1d",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {response && (
        <div
          data-testid="haiku-response"
          style={{
            marginTop: 12,
            padding: 12,
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
          }}
        >
          <div
            data-testid="haiku-draft-badge"
            style={{
              display: "inline-block",
              padding: "2px 8px",
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: 999,
              fontSize: 11,
              color: "#78350f",
              marginBottom: 8,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            Draft, not legal advice
          </div>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, color: "#0f172a" }}>
            {response.response_text}
          </div>
          {response.cited_engine_rule && (
            <div
              data-testid="haiku-engine-citation"
              style={{ marginTop: 8, fontSize: 12, color: "#334155" }}
            >
              Engine rule cited: <code>{response.cited_engine_rule}</code>
            </div>
          )}
          {response.parked_field_id && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#4338ca" }}>
              Parked as suggestion for {response.parked_field_id}.
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
